import {
  DEFAULT_PAGER,
  FilterOptionType,
  FilterType,
  FilterTypeOperator,
  GetListApiFilter,
  RenderTableFilterOptionProps,
} from '@monetize/types/common';
import { useEffect, useState } from 'react';
import { useAtom } from 'jotai';
import { useGetQuoteApprovalDashboard } from '../../../../api/approvalService';
import {
  MBox,
  MPageContentContainer,
  MPageHeader,
  MSkeleton,
  MStack,
} from '../../../../components/Monetize';
import {
  AmountTableFilterOptionContent,
  MDataTableFilter,
} from '../../../../components/Monetize/DataTable';
import { DaysPendingApprovalFilterOptionContent } from '../../../../components/Monetize/DataTable/FilterOptions/DaysPendingApprovalFilterOptionContent';
import MEmptyDataPlaceholder from '../../../../components/Monetize/MEmptyDataPlaceholder';
import { DashboardQuoteProgress } from '../../../../components/Quotes/QuoteProgress/DashboardQuoteProgress';
import { tableFilterSelector } from '../../../../store/global.store';
import { FilterStateKeys } from '../../../../store/store.types';
import {
  buildFilterParamsRequestObject,
  getIsTrulyEmptyList,
} from '../../../../utils';

const generateFilterParams = ({
  filters,
  createdByFilter,
  currency,
}: {
  filters: FilterType[];
  createdByFilter?: { userId?: null | string; teamId?: null | string };
  currency?: string;
}) => {
  const newFilters = [...filters];
  if (currency) {
    newFilters.push({
      key: 'currency',
      operator: FilterTypeOperator.EQUAL,
      value: currency,
    });
  }

  if (createdByFilter?.userId) {
    newFilters.push({
      key: 'userId',
      operator: FilterTypeOperator.EQUAL,
      value: createdByFilter.userId,
    });
  }

  if (createdByFilter?.teamId) {
    newFilters.push({
      key: 'teamId',
      operator: FilterTypeOperator.EQUAL,
      value: createdByFilter.teamId,
    });
  }

  newFilters.push({
    // UI need only Pending approvals
    key: 'notApproved',
    operator: FilterTypeOperator.EQUAL,
    value: 'true',
  });

  return buildFilterParamsRequestObject(newFilters);
};
export interface ApprovalDashboardProps {
  createdByFilter: { userId?: null | string; teamId?: null | string };
  resetAllFilters: () => void;
}

export const ApprovalDashboard = ({
  createdByFilter,
  resetAllFilters,
}: ApprovalDashboardProps) => {
  const [initialFilters, persistTableFilter] = useAtom(
    tableFilterSelector(FilterStateKeys.DASHBOARD_APPROVAL_LIST),
  );
  const [filters, setFilters] = useState<FilterType[]>(initialFilters);
  const [filterParams, setFilterParams] = useState<GetListApiFilter>(() =>
    generateFilterParams({
      filters,
      createdByFilter,
    }),
  );

  useEffect(() => {
    setFilterParams(() =>
      generateFilterParams({
        filters,
        createdByFilter,
      }),
    );
  }, [filters, createdByFilter]);

  useEffect(() => {
    persistTableFilter(filters);
  }, [filters, persistTableFilter]);

  const filterOptions: FilterOptionType[] = [
    {
      title: 'Amount',
      key: 'amount',
      operator: FilterTypeOperator.GLTE,
      renderOptionContent: (props: RenderTableFilterOptionProps) => {
        return <AmountTableFilterOptionContent {...props} />;
      },
    },
    {
      title: 'Days in Approval',
      key: 'daysPendingApproval',
      operator: FilterTypeOperator.EQUAL,
      renderOptionContent: (props: RenderTableFilterOptionProps) => {
        return <DaysPendingApprovalFilterOptionContent {...props} />;
      },
    },
  ];

  const onResetApprovalFilter = () => {
    setFilters([]);
  };
  const onResetAllFilter = () => {
    setFilters([]);
    resetAllFilters();
  };

  const {
    isLoading,
    isRefetching,
    isFetched,
    data: quoteList,
    refetch,
  } = useGetQuoteApprovalDashboard({
    config: DEFAULT_PAGER,
    filters: filterParams,
  });

  const loading = isLoading || (isRefetching && !isFetched);
  const isTrulyEmptyList = getIsTrulyEmptyList({
    loading,
    filters,
    page: DEFAULT_PAGER.page,
    totalElements: quoteList?.length || 0,
  });

  if (loading) {
    return (
      <MBox w="100%">
        <MStack gap="2" w="100%">
          <MSkeleton height="200" w="100%" />
          <MSkeleton height="200" w="100%" />
          <MSkeleton height="200" w="100%" />
        </MStack>
      </MBox>
    );
  }

  return (
    <MPageContentContainer w="100%">
      <MPageHeader title="Pending Approvals">
        {!isTrulyEmptyList && (
          <MDataTableFilter
            filters={filters}
            filterOptions={filterOptions}
            setFilters={setFilters}
            onResetFilter={onResetApprovalFilter}
          />
        )}
      </MPageHeader>

      <MStack gap="4" w="100%">
        {quoteList?.map((quote) => (
          <DashboardQuoteProgress
            key={quote.id}
            quote={quote}
            approvals={(quote as any).approvals}
            reloadData={refetch}
          />
        ))}
        {quoteList?.length === 0 && (
          <MEmptyDataPlaceholder
            mainMessage="No pending approvals."
            smallMessage="Approvals needing review will appear here."
            btnLabel="Clear Filters"
            onClick={onResetAllFilter}
          />
        )}
      </MStack>
    </MPageContentContainer>
  );
};
